import {
  Alert,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { DataType, DeviceData } from "graphql/types"
import { SyntheticEvent, useEffect, useState } from "react"
import {
  convertLabelsToValues,
  convertValuesToLabels,
  getUnitListForAutocomplete,
} from "utils/constants"

type AddDeviceDataDialogProps = {
  open: boolean
  onClose: () => void
  formState: DeviceData
  onSubmit: (arg: DeviceData) => void
}

type ValidationErrors = {
  name?: string
  dataType?: string
}

// Funktion zur Übersetzung der technischen Datentypen in benutzerfreundliche Bezeichnungen
const getDataTypeLabel = (type: DataType): string => {
  switch (type) {
    case DataType.STRING:
      return "Text"
    case DataType.FLOAT:
      return "Zahl"
    case DataType.BOOLEAN:
      return "Boolean"
    default:
      return type
  }
}

// Funktion zur Umkehrung der Übersetzung (von Label zu technischem Typ)
const getDataTypeFromLabel = (label: string): DataType => {
  switch (label) {
    case "Text":
      return DataType.STRING
    case "Zahl":
      return DataType.FLOAT
    case "Boolean":
      return DataType.BOOLEAN
    default:
      return DataType.STRING // Fallback
  }
}

export const AddDeviceDataDialog = (props: AddDeviceDataDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const [localFormState, setLocalFormState] = useState<DeviceData>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  useEffect(() => {
    let updatedState = { ...localFormState }
    if (localFormState.dataType === DataType.BOOLEAN) {
      updatedState.isArray = false
    }
    if (localFormState.dataType !== DataType.FLOAT) {
      updatedState.isCalculation = false
      updatedState.isOperatingMode = false
    }
    setLocalFormState(updatedState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFormState.dataType])

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

    // Clear validation error for this field
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name as keyof ValidationErrors]
        return newErrors
      })
    }
  }

  const handleFormChangeUnit = (_: SyntheticEvent<Element, Event>, value: string[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      unit: convertLabelsToValues(value),
    }))
  }

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    if (!localFormState.dataType) {
      errors.dataType = "Datentyp ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  // Erstelle benutzerfreundliche Optionen für das Autocomplete
  const dataTypeOptions = Object.values(DataType).map((type) => getDataTypeLabel(type))

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>
          {localFormState.id === "NEW"
            ? "EUT-Eigenschaft hinzufügen"
            : "EUT-Eingenschaft bearbeiten"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {hasValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Bitte korrigieren Sie die folgenden Fehler:
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(validationErrors).map(([field, error]) => (
                  <Typography key={field} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <TextField
            size="small"
            name="name"
            label="Name"
            type="text"
            fullWidth
            value={localFormState.name}
            onChange={handleTextChange}
            sx={{ mt: 1 }}
            required
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />

          <Autocomplete
            size="small"
            options={dataTypeOptions}
            value={getDataTypeLabel(localFormState.dataType)}
            onChange={(_, newValue) => {
              setLocalFormState((prevState) => ({
                ...prevState,
                dataType: newValue ? getDataTypeFromLabel(newValue) : DataType.STRING,
              }))

              // Clear validation error for this field
              if (validationErrors.dataType) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev }
                  delete newErrors.dataType
                  return newErrors
                })
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Datentyp"
                fullWidth
                required
                error={!!validationErrors.dataType}
                helperText={validationErrors.dataType}
              />
            )}
            fullWidth
          />

          <AutocompleteMulti
            options={getUnitListForAutocomplete()}
            value={convertValuesToLabels(localFormState.unit)}
            onChange={handleFormChangeUnit}
            label={"Einheiten"}
            isEnum={true}
          />

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFormState.isArray}
                  onChange={(e) =>
                    setLocalFormState((prevState) => ({
                      ...prevState,
                      isArray: e.target.checked,
                    }))
                  }
                  disabled={localFormState.dataType === DataType.BOOLEAN}
                />
              }
              label="Mehrfachwerte zulassen"
            />
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFormState.isCalculation}
                  onChange={(e) =>
                    setLocalFormState((prevState) => ({
                      ...prevState,
                      isCalculation: e.target.checked,
                    }))
                  }
                  disabled={localFormState.dataType !== DataType.FLOAT}
                />
              }
              label="Kalkulationsrelevant"
            />
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFormState.isOperatingMode && localFormState.isArray}
                  onChange={(e) =>
                    setLocalFormState((prevState) => ({
                      ...prevState,
                      isOperatingMode: e.target.checked,
                    }))
                  }
                  disabled={!localFormState.isArray}
                />
              }
              label="Diese EUT Eigenschaft ist der Betriebszustand"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={handleClose} variant="contained" />
          <StyledButton
            buttonType="save"
            onClick={handleSave}
            variant="contained"
            disabled={hasValidationErrors}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
